import { useState, useRef } from 'react';
import { uploadImage, addVideoUrl, generateExerciseImage, getMediaFileUrl } from '../api/media';
import MediaLibraryPicker from './MediaLibraryPicker';

const MAX_IMAGES = 5;
const MAX_EXPL_VIDEOS = 3;

export default function ExerciseMediaSection({
  exerciseName,
  existingMedia = [],
  pendingMedia = [],
  onAddPending,
  onRemoveExisting,
  onRemovePending,
}) {
  const [explVideoUrl, setExplVideoUrl] = useState('');
  const [presVideoUrl, setPresVideoUrl] = useState('');
  const [explVideoError, setExplVideoError] = useState('');
  const [presVideoError, setPresVideoError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const fileInputRef = useRef();

  function getEmbedUrl(media) {
    const info = media.meta_info;
    if (!info) return null;
    if (info.platform === 'youtube') return `https://www.youtube.com/embed/${info.video_id}`;
    if (info.platform === 'vimeo') return `https://player.vimeo.com/video/${info.video_id}`;
    return null;
  }

  // ── Photo upload ─────────────────────────────────────────────────────────
  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError('');
    if (imageCount >= MAX_IMAGES) {
      setUploadError(`Maximal ${MAX_IMAGES} Bilder pro Übung erlaubt.`);
      e.target.value = '';
      return;
    }
    const localPreviewUrl = URL.createObjectURL(file);
    try {
      const media = await uploadImage(file);
      onAddPending({ media, mediaType: 'image', previewUrl: localPreviewUrl });
    } catch (err) {
      URL.revokeObjectURL(localPreviewUrl);
      setUploadError(err.message);
    }
    e.target.value = '';
  }

  // ── Explanation video ────────────────────────────────────────────────────
  async function handleAddExplVideo() {
    setExplVideoError('');
    if (!explVideoUrl.trim()) return;
    if (explVideoCount >= MAX_EXPL_VIDEOS) {
      setExplVideoError(`Maximal ${MAX_EXPL_VIDEOS} Erklärungsvideos erlaubt.`);
      return;
    }
    try {
      const media = await addVideoUrl(explVideoUrl.trim());
      onAddPending({ media, mediaType: 'video' });
      setExplVideoUrl('');
    } catch (err) {
      setExplVideoError(err.message);
    }
  }

  // ── Presenter video ──────────────────────────────────────────────────────
  async function handleAddPresVideo() {
    setPresVideoError('');
    if (!presVideoUrl.trim()) return;
    if (presenterVideoCount >= 1) {
      setPresVideoError('Maximal 1 Ablaufvideo erlaubt.');
      return;
    }
    try {
      const media = await addVideoUrl(presVideoUrl.trim());
      onAddPending({ media, mediaType: 'presenter_video' });
      setPresVideoUrl('');
    } catch (err) {
      setPresVideoError(err.message);
    }
  }

  // ── AI image ─────────────────────────────────────────────────────────────
  async function handleAiGenerate() {
    if (!exerciseName?.trim()) {
      setAiError('Bitte zuerst den Namen der Übung eingeben.');
      return;
    }
    if (imageCount >= MAX_IMAGES) {
      setAiError(`Maximal ${MAX_IMAGES} Bilder pro Übung erlaubt.`);
      return;
    }
    setAiLoading(true);
    setAiError('');
    try {
      const result = await generateExerciseImage(exerciseName);
      const media = {
        id: result.media_id,
        type: 'image',
        file_name: `${exerciseName}_ai.png`,
        file_path: result.file_path,
        mime_type: 'image/png',
        meta_info: { source: 'openai_dalle3' },
      };
      onAddPending({ media, mediaType: 'image' });
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  }

  // ── Aus Bibliothek wählen ─────────────────────────────────────────────────
  function handlePickFromLibrary(media) {
    setShowPicker(false);
    setUploadError('');
    if (imageCount >= MAX_IMAGES) {
      setUploadError(`Maximal ${MAX_IMAGES} Bilder pro Übung erlaubt.`);
      return;
    }
    const already = allImages.some((it) => (it.media?.id ?? it.media_id) === media.id);
    if (already) {
      setUploadError('Dieses Bild ist bereits hinzugefügt.');
      return;
    }
    onAddPending({ media, mediaType: 'image' });
  }

  // ── Counts ────────────────────────────────────────────────────────────────
  const allImages = [
    ...existingMedia.filter((m) => (m.media?.type || m.type) === 'image'),
    ...pendingMedia.filter((p) => p.mediaType === 'image'),
  ];
  const allExplVideos = [
    ...existingMedia.filter((m) => m.media_type === 'video'),
    ...pendingMedia.filter((p) => p.mediaType === 'video'),
  ];
  const allPresVideos = [
    ...existingMedia.filter((m) => m.media_type === 'presenter_video'),
    ...pendingMedia.filter((p) => p.mediaType === 'presenter_video'),
  ];
  const imageCount = allImages.length;
  const explVideoCount = allExplVideos.length;
  const presenterVideoCount = allPresVideos.length;

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h6 className="card-subtitle mb-3 text-muted fw-semibold">Medien</h6>

        {/* ── Images ── */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
            <span className="fw-semibold small">
              <i className="bi bi-image me-1 text-primary" />Bilder
              <span className={`badge ms-2 ${imageCount >= MAX_IMAGES ? 'bg-danger' : 'bg-secondary'}`}>
                {imageCount}/{MAX_IMAGES}
              </span>
            </span>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={imageCount >= MAX_IMAGES}
              >
                <i className="bi bi-upload me-1" />Foto hochladen
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowPicker(true)}
                disabled={imageCount >= MAX_IMAGES}
              >
                <i className="bi bi-images me-1" />Aus Bibliothek
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={handleAiGenerate}
                disabled={aiLoading || imageCount >= MAX_IMAGES}
              >
                {aiLoading ? (
                  <><span className="spinner-border spinner-border-sm me-1" />KI generiert…</>
                ) : (
                  <><i className="bi bi-stars me-1" />KI-Bild generieren</>
                )}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="d-none"
              onChange={handleFileChange}
            />
          </div>
          {uploadError && <div className="alert alert-danger py-1 small mb-2">{uploadError}</div>}
          {aiError && <div className="alert alert-danger py-1 small mb-2">{aiError}</div>}
          {allImages.length === 0 ? (
            <p className="text-muted small fst-italic">Noch kein Bild hinzugefügt.</p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {existingMedia
                .filter((m) => (m.media?.type || m.type) === 'image')
                .map((rel) => (
                  <MediaImageThumb
                    key={`ex-${rel.id}`}
                    src={getMediaFileUrl(rel.media?.id || rel.media_id)}
                    isAi={rel.media?.meta_info?.source === 'openai_dalle3'}
                    onRemove={() => onRemoveExisting(rel.id)}
                  />
                ))}
              {pendingMedia
                .filter((p) => p.mediaType === 'image')
                .map((p) => (
                  <MediaImageThumb
                    key={`pend-${p.media.id}`}
                    src={p.previewUrl || getMediaFileUrl(p.media.id)}
                    isAi={p.media.meta_info?.source === 'openai_dalle3'}
                    isPending
                    onRemove={() => onRemovePending(p.media.id)}
                  />
                ))}
            </div>
          )}
        </div>

        {/* ── Explanation videos ── */}
        <div className="mb-4">
          <div className="d-flex align-items-center mb-1 gap-2">
            <span className="fw-semibold small">
              <i className="bi bi-camera-video me-1 text-secondary" />
              Erklärungsvideos
              <span className="ms-1 text-muted" style={{ fontWeight: 400 }}>(Technik, verschiedene Winkel)</span>
              <span className={`badge ms-2 ${explVideoCount >= MAX_EXPL_VIDEOS ? 'bg-danger' : 'bg-secondary'}`}>
                {explVideoCount}/{MAX_EXPL_VIDEOS}
              </span>
            </span>
          </div>
          <div className="input-group mb-2" style={{ maxWidth: 500 }}>
            <input
              type="url"
              className="form-control form-control-sm"
              placeholder="YouTube- oder Vimeo-URL"
              value={explVideoUrl}
              disabled={explVideoCount >= MAX_EXPL_VIDEOS}
              onChange={(e) => setExplVideoUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddExplVideo())}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={explVideoCount >= MAX_EXPL_VIDEOS}
              onClick={handleAddExplVideo}
            >
              <i className="bi bi-plus-lg" />
            </button>
          </div>
          {explVideoError && <div className="alert alert-danger py-1 small mb-2">{explVideoError}</div>}
          {allExplVideos.length > 0 && (
            <div className="d-flex flex-wrap gap-3">
              {existingMedia
                .filter((m) => m.media_type === 'video')
                .map((rel) => {
                  const mediaObj = rel.media || rel;
                  return (
                    <MediaVideoThumb
                      key={`exv-${rel.id}`}
                      embedUrl={getEmbedUrl(mediaObj)}
                      platform={mediaObj.meta_info?.platform}
                      onRemove={() => onRemoveExisting(rel.id)}
                    />
                  );
                })}
              {pendingMedia
                .filter((p) => p.mediaType === 'video')
                .map((p) => (
                  <MediaVideoThumb
                    key={`pendv-${p.media.id}`}
                    embedUrl={getEmbedUrl(p.media)}
                    platform={p.media.meta_info?.platform}
                    isPending
                    onRemove={() => onRemovePending(p.media.id)}
                  />
                ))}
            </div>
          )}
        </div>

        {/* ── Presenter video ── */}
        <div>
          <div className="d-flex align-items-center mb-1 gap-2">
            <span className="fw-semibold small">
              <i className="bi bi-play-circle me-1 text-danger" />
              Ablaufvideo
              <span className="ms-1 text-muted" style={{ fontWeight: 400 }}>(wird im Presenter automatisch abgespielt und wiederholt)</span>
              <span className={`badge ms-2 ${presenterVideoCount >= 1 ? 'bg-danger' : 'bg-secondary'}`}>
                {presenterVideoCount}/1
              </span>
            </span>
          </div>
          {presenterVideoCount < 1 && (
            <div className="input-group mb-2" style={{ maxWidth: 500 }}>
              <input
                type="url"
                className="form-control form-control-sm"
                placeholder="YouTube- oder Vimeo-URL"
                value={presVideoUrl}
                onChange={(e) => setPresVideoUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPresVideo())}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={handleAddPresVideo}
              >
                <i className="bi bi-plus-lg" />
              </button>
            </div>
          )}
          {presVideoError && <div className="alert alert-danger py-1 small mb-2">{presVideoError}</div>}
          {allPresVideos.length > 0 && (
            <div className="d-flex flex-wrap gap-3">
              {existingMedia
                .filter((m) => m.media_type === 'presenter_video')
                .map((rel) => {
                  const mediaObj = rel.media || rel;
                  return (
                    <MediaVideoThumb
                      key={`prv-${rel.id}`}
                      embedUrl={getEmbedUrl(mediaObj)}
                      platform={mediaObj.meta_info?.platform}
                      badge="Ablauf"
                      onRemove={() => onRemoveExisting(rel.id)}
                    />
                  );
                })}
              {pendingMedia
                .filter((p) => p.mediaType === 'presenter_video')
                .map((p) => (
                  <MediaVideoThumb
                    key={`pendpv-${p.media.id}`}
                    embedUrl={getEmbedUrl(p.media)}
                    platform={p.media.meta_info?.platform}
                    badge="Ablauf"
                    isPending
                    onRemove={() => onRemovePending(p.media.id)}
                  />
                ))}
            </div>
          )}
        </div>

      </div>

      <MediaLibraryPicker
        show={showPicker}
        onPick={handlePickFromLibrary}
        onClose={() => setShowPicker(false)}
      />
    </div>
  );
}

function MediaImageThumb({ src, isAi, isPending, onRemove }) {
  return (
    <div className="position-relative" style={{ width: 100, height: 100 }}>
      <img src={src} alt="" className="rounded border w-100 h-100" style={{ objectFit: 'cover' }} />
      {isAi && (
        <span className="position-absolute top-0 start-0 badge bg-primary" style={{ fontSize: '0.6rem' }}>KI</span>
      )}
      {isPending && (
        <span className="position-absolute top-0 end-0 badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>neu</span>
      )}
      <button
        type="button"
        className="btn btn-danger btn-sm position-absolute bottom-0 end-0 p-0"
        style={{ width: 22, height: 22, lineHeight: 1 }}
        onClick={onRemove}
        title="Entfernen"
      >
        <i className="bi bi-x" />
      </button>
    </div>
  );
}

function MediaVideoThumb({ embedUrl, platform, badge, isPending, onRemove }) {
  const icon = platform === 'youtube' ? 'bi-youtube text-danger' : 'bi-vimeo text-info';
  return (
    <div className="position-relative border rounded overflow-hidden" style={{ width: 200, height: 120 }}>
      {embedUrl ? (
        <iframe
          src={embedUrl}
          title="Video preview"
          width="200"
          height="120"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ display: 'block' }}
        />
      ) : (
        <div className="d-flex align-items-center justify-content-center h-100" style={{ background: 'var(--color-surface-2)' }}>
          <i className={`bi ${icon} fs-2`} />
        </div>
      )}
      {badge && (
        <span className="position-absolute top-0 start-0 badge bg-danger" style={{ fontSize: '0.6rem' }}>{badge}</span>
      )}
      {isPending && (
        <span className="position-absolute top-0 end-0 badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>neu</span>
      )}
      <button
        type="button"
        className="btn btn-danger btn-sm position-absolute top-0 end-0 p-0"
        style={{ width: 22, height: 22, lineHeight: 1 }}
        onClick={onRemove}
        title="Entfernen"
      >
        <i className="bi bi-x" />
      </button>
    </div>
  );
}
